import { UserCapacity } from '@wui/common-library'
import { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { useApi } from '@/composables/useApi'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useUserData, UserFields } from '@/shared/composables/local-storage'
import { useLocalization } from '@/shared/composables/localization'
import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'

import { getIsEnoughCapacity, VoiceMailMessage, WhoAmIInfo } from '..'

export const useStoreProfile = defineStore('user-profile', () => {
  const voiceMailMessages = ref<Array<VoiceMailMessage>>([])
  const voiceMailLoading = ref(false)
  const capacity = ref<UserCapacity | undefined>()
  const isVideoCodecsEnabled = ref(false)

  const isEnoughCapacity = computed(() => getIsEnoughCapacity(capacity.value))

  const fetchProfile = async () => {
    try {
      const { data } = await useApi().getWhoAmI<WhoAmIInfo>()
      const { setCurrentUserField } = useCurrentUser()
      setCurrentUserField('terminalLogin', data.terminalLogin)
      setCurrentUserField('terminalPassword', data.terminalPassword)
      setCurrentUserField('webSoftphoneEnabled', data.webSoftphoneEnabled)

      useUserData().setField(UserFields.PIN_CODE, data.pinCode)

      if (!data.webSoftphoneEnabled) {
        console.warn('Web softphone is disabled')
      }

      if (data.capacity) {
        setCapacity(data.capacity)
      }

      isVideoCodecsEnabled.value = data.isVideoCodecsEnabled === undefined ? true : data.isVideoCodecsEnabled
    } catch {
      const { t } = useLocalization()
      throw new Error(t('profile.errorOnFetchingProfile'))
    }
  }

  const fetchVoiceMailMessages = async (): Promise<VoiceMailMessage[]> => {
    try {
      const fetch = useFetchApi()
      const { data } = await fetch.get<VoiceMailMessage[]>(getAppURL({ pathName: '/api/voice-mail-messages' }))
      voiceMailMessages.value = data

      return voiceMailMessages.value
    } catch {
      const { t } = useLocalization()
      throw new Error(t('profile.errorOnFetchingVoiceMailMessages'))
    }
  }

  const removeVoiceMailMessage = async (guid: string): Promise<boolean> => {
    try {
      const fetch = useFetchApi()
      await fetch.delete(getAppURL({ pathName: `/api/voice-mail-messages/${guid}` }))

      return true
    } catch {
      const { t } = useLocalization()
      throw new Error(t('profile.errorOnRemoveVoiceMailMessages'))
    }
  }

  const copyToAudioFiles = async (guid: string): Promise<boolean> => {
    try {
      const fetch = useFetchApi()
      const res = await fetch.post(getAppURL({ pathName: `/api/voice-mail-messages/${guid}/copy-to-audios` }))
      return res.status === 204
    } catch (e: unknown) {
      const { t } = useLocalization()
      if (e instanceof AxiosError) {
        throw e
      }
      const errorMessage = e instanceof Error ? e.message : t('profile.errorOnRemoveVoiceMailMessages')
      throw new Error(errorMessage)
    }
  }

  const removeVoiceMailMessages = async (): Promise<boolean> => {
    try {
      const fetch = useFetchApi()
      await fetch.delete(getAppURL({ pathName: '/api/voice-mail-messages/' }))

      return true
    } catch {
      const { t } = useLocalization()
      throw new Error(t('profile.errorOnRemoveVoiceMailMessages'))
    }
  }

  const setVoiceMailLoading = (value: boolean): void => {
    voiceMailLoading.value = value
  }

  const setCapacity = (c: UserCapacity) => {
    capacity.value = c
  }

  return {
    fetchProfile,
    fetchVoiceMailMessages,
    removeVoiceMailMessage,
    removeVoiceMailMessages,
    copyToAudioFiles,
    setVoiceMailLoading,
    setCapacity,
    voiceMailMessages,
    voiceMailLoading,
    isEnoughCapacity,
    isVideoCodecsEnabled,
  }
})
